"use client";

import { useEffect } from "react";
import { useJsonResource } from "@/hooks/useJsonResource";
import type { KnowledgeGap } from "@/lib/knowledgeGaps/types";
import type { KnowledgeFolder, KnowledgePageFolder } from "@/lib/kb/folderTypes";

/**
 * Knowledge-base pages for the knowledge panel. Re-fetched when the business
 * changes, on KB edits, and on general activity refreshes.
 */
export function useKnowledgePages(enabled: boolean, activeBusiness: string) {
  const { data, refresh } = useJsonResource<{
    businessId: string;
    pages: string[];
    rawSources: string[];
    folders?: KnowledgeFolder[];
    pageFolders?: KnowledgePageFolder[];
  }>(enabled && activeBusiness ? `/api/kb?${new URLSearchParams({ businessId: activeBusiness })}` : null, { interval: 30000 });
  const { data: gapData, refresh: refreshGaps } = useJsonResource<{ businessId: string; gaps: KnowledgeGap[] }>(
    enabled && activeBusiness ? `/api/kb/gaps?${new URLSearchParams({ businessId: activeBusiness })}` : null,
    { interval: 15000 }
  );
  useEffect(() => {
    const onRefresh = () => { void refresh(); void refreshGaps(); };
    onRefresh();
    window.addEventListener("caw:kb-refresh", onRefresh);
    window.addEventListener("caw:activity-refresh", onRefresh);
    return () => {
      window.removeEventListener("caw:kb-refresh", onRefresh);
      window.removeEventListener("caw:activity-refresh", onRefresh);
    };
  }, [activeBusiness, refresh, refreshGaps]);
  const scoped = data?.businessId === activeBusiness ? data : undefined;
  return { pages: scoped?.pages ?? [], rawSources: scoped?.rawSources ?? [], folders: scoped?.folders ?? [], pageFolders: scoped?.pageFolders ?? [], refresh, gapCount: gapData?.businessId === activeBusiness ? gapData.gaps.filter(gap => gap.status === "open").length : undefined };
}
